import React, { useState, useEffect, useCallback } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import utils from "../../universal/utils";
import adminFetch from "../../universal/adminFetch";
import CannedResponseChips from "./CannedResponseChips";
import { NOT_ELIGIBLE_CANNED_RESPONSES } from "./cannedReviewResponses";

const basePath = utils.getEdgeBasePath();

function formatDate(d) {
  if (!d) return "";
  return new Date(d).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function EligibilityThreadDialog({ open, userContext, adminUser, onClose }) {
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [replyContent, setReplyContent] = useState("");
  const [replying, setReplying] = useState(false);
  const [replyError, setReplyError] = useState("");
  const [replySuccess, setReplySuccess] = useState(false);

  const targetUserId = userContext?.userId;
  const adminUserId = adminUser?.userId;

  const fetchThread = useCallback(() => {
    if (!targetUserId || !adminUserId) return;
    setLoading(true);
    setError("");
    adminFetch(
      `${basePath}/api/v7/admin/user/eligibility-thread/${targetUserId}/adpass/${adminUserId}`
    )
      .then((r) => r.json())
      .then((data) => {
        if (data.ok) {
          setThread(data.thread || null);
        } else {
          setError(data.error || "Failed to load thread");
        }
      })
      .catch(() => setError("Network error. Please try again."))
      .finally(() => setLoading(false));
  }, [targetUserId, adminUserId]);

  useEffect(() => {
    if (open) {
      setThread(null);
      setReplyContent("");
      setReplyError("");
      setReplySuccess(false);
      fetchThread();
    }
  }, [open, fetchThread]);

  const handleSendReply = async () => {
    const trimmed = replyContent.trim();
    if (!trimmed) return;
    setReplying(true);
    setReplyError("");
    setReplySuccess(false);
    try {
      const res = await adminFetch(
        `${basePath}/api/v7/admin/user/eligibility-thread/${targetUserId}/reply/adpass/${adminUserId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ content: trimmed }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setReplyError(data.error || "Failed to send reply. Please try again.");
      } else {
        setReplyContent("");
        setReplySuccess(true);
        fetchThread();
        setTimeout(() => setReplySuccess(false), 5000);
      }
    } catch {
      setReplyError("Network error. Please try again.");
    } finally {
      setReplying(false);
    }
  };

  const nickname = userContext?.row?.nickname || targetUserId || "";
  const allMessages = thread?.messages || [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Eligibility Thread — {nickname}
        {thread && (
          <Chip
            size="small"
            label={thread.status === "closed" ? "Closed" : "Open"}
            style={{
              marginLeft: 10,
              backgroundColor: thread.status === "closed" ? "#555" : "#2e7d32",
              color: "#fff",
              fontSize: 11,
              verticalAlign: "middle",
            }}
          />
        )}
      </DialogTitle>

      <DialogContent dividers style={{ minHeight: 200 }}>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        {!loading && !error && !thread && (
          <Typography
            variant="body2"
            style={{ color: "rgba(255,255,255,0.5)", fontStyle: "italic" }}
          >
            No eligibility thread exists for this player yet. Mark the player as Not Eligible
            to start one.
          </Typography>
        )}

        {!loading && thread && (
          <>
            {thread.notEligibleReason && (
              <Box
                style={{
                  marginBottom: 16,
                  padding: "10px 14px",
                  borderLeft: "3px solid #c62828",
                  backgroundColor: "rgba(198,40,40,0.06)",
                  borderRadius: 4,
                }}
              >
                <Typography variant="caption" style={{ color: "#ef9a9a", fontWeight: 700 }}>
                  Current Not-Eligible Reason
                </Typography>
                <Typography variant="body2" style={{ marginTop: 4 }}>
                  {thread.notEligibleReason}
                </Typography>
              </Box>
            )}

            {allMessages.length === 0 && (
              <Typography
                variant="body2"
                style={{ color: "rgba(255,255,255,0.45)", fontStyle: "italic" }}
              >
                No messages yet.
              </Typography>
            )}

            <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
              {allMessages.map((msg, i) => {
                const isAdmin = msg.author === "admin";
                const isStatus = msg.isStatusUpdate;
                return (
                  <Box
                    key={i}
                    style={{
                      padding: "10px 14px",
                      borderRadius: 8,
                      maxWidth: "90%",
                      alignSelf: isAdmin ? "flex-start" : "flex-end",
                      backgroundColor: isStatus
                        ? "rgba(198,40,40,0.07)"
                        : isAdmin
                        ? "rgba(255,117,0,0.08)"
                        : "rgba(255,255,255,0.05)",
                      border: `1px solid ${
                        isStatus
                          ? "rgba(198,40,40,0.18)"
                          : isAdmin
                          ? "rgba(255,117,0,0.18)"
                          : "rgba(255,255,255,0.08)"
                      }`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      style={{
                        display: "block",
                        fontWeight: 700,
                        marginBottom: 4,
                        color: isStatus ? "#ef9a9a" : isAdmin ? "#FF7500" : "rgba(255,255,255,0.45)",
                      }}
                    >
                      {isStatus
                        ? `[Status Update] ${msg.authorName || "Ouro Team"}`
                        : isAdmin
                        ? msg.authorName || "Admin"
                        : "Player"}{" "}
                      &middot; {formatDate(msg.timestamp)}
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{ whiteSpace: "pre-wrap", lineHeight: 1.55 }}
                    >
                      {msg.content}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            {thread.status !== "closed" && (
              <Box style={{ marginTop: 20 }}>
                <CannedResponseChips
                  presets={NOT_ELIGIBLE_CANNED_RESPONSES}
                  value={replyContent}
                  onToggle={({ nextValue }) => {
                    setReplyContent(nextValue);
                    if (replyError) setReplyError("");
                  }}
                />
                <TextField
                  label="Your reply"
                  placeholder="Type a reply to the player..."
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={6}
                  variant="outlined"
                  value={replyContent}
                  onChange={(e) => {
                    setReplyContent(e.target.value);
                    if (replyError) setReplyError("");
                  }}
                  disabled={replying}
                  inputProps={{ maxLength: 3000 }}
                />
                {replyError && (
                  <Typography variant="caption" style={{ color: "#f44336", display: "block", marginTop: 4 }}>
                    {replyError}
                  </Typography>
                )}
                {replySuccess && (
                  <Typography
                    variant="caption"
                    style={{ color: "#4caf50", display: "block", marginTop: 4 }}
                  >
                    Reply sent. The player will receive an email notification.
                  </Typography>
                )}
              </Box>
            )}

            {thread.status === "closed" && (
              <Typography
                variant="body2"
                style={{ color: "rgba(255,255,255,0.45)", fontStyle: "italic", marginTop: 12 }}
              >
                This thread is closed.
              </Typography>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default">
          Close
        </Button>
        {thread && thread.status !== "closed" && (
          <Button
            onClick={handleSendReply}
            color="primary"
            variant="contained"
            disabled={replying || !replyContent.trim()}
          >
            {replying ? <CircularProgress size={18} /> : "Send Reply"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
